import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, ChangeDetectorRef, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { TenantEmployeeComponent } from '../../../tenant/tenant-employee/tenant-employee.component';

@Component({
  selector: 'servicenumber-member',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzDividerModule,
    NzGridModule,
    NzIconModule,
    NzAvatarModule,
    NzTableModule,
    NzTagModule,
    NzModalModule,
    NzPopconfirmModule,
    NzEmptyModule,
    I18nPipe,
    NzToolTipModule,
    TestIdDirective
  ],
  templateUrl: './servicenumber-member.component.html',
  styleUrls: ['./servicenumber-member.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceMemberComponent implements OnInit {
  @Input() serviceNumberId: string = '';
  @Input() tenantId: string = '';
  @Input() tenantType: string = ''; // 新增租戶類型輸入
  @Input() isAddSN: boolean = false;
  @Input() inlineAddMode: boolean = false;
  @Output() readonly membersChange = new EventEmitter<any[]>();

  // 注入 ModalService
  private readonly modalService = inject(NzModalService);
  localMembers: any[] = [];
  hasOwner: boolean = false;

  // 成员列表数据
  memberList: any[] = [];
  loading = false;

  // 权限选项
  privilegeOptions: any[] = [];
  isConfirmLoading = false;
  isModalVisible = false;
  isEditing = false;
  currentMemberId: string = '';
  // 添加/编辑成员表单
  memberForm!: FormGroup;

  pageSize = 10;
  pageIndex = 1;
  total = 0;
  refreshTime = Date.now();
  direction = 'desc';
  memberSearchKeyword = '';
  availableEmployees: any[] = [];
  employeeLoading = false;
  employeePageIndex = 1;
  employeePageSize = 20;
  employeeTotal = 0;
  hasMoreEmployees = true;
  private memberListRequestId = 0;

  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  ngOnInit(): void {
    this.initPrivilegeOptions();
    this.initForm();
    if (this.isAddSN && this.inlineAddMode) {
      this.resetAvailableEmployees();
    }
    if (this.serviceNumberId && !this.isAddSN) {
      this.loadMemberList();
    }
  }

  get selectedOwnerCount(): number {
    return this.localMembers.filter(member => member.privilege === 'Owner').length;
  }

  get selectedManagerCount(): number {
    return this.localMembers.filter(member => member.privilege === 'Manager').length;
  }

  get selectedCommonCount(): number {
    return this.localMembers.filter(member => member.privilege === 'Common').length;
  }

  get selectableEmployees(): any[] {
    const selectedIds = new Set(this.localMembers.map(member => member.memberId));
    return this.availableEmployees.filter(employee => !selectedIds.has(employee.id));
  }

  // 初始化权限选项
  initPrivilegeOptions(): void {
    this.privilegeOptions = [
      { label: this.i18n.fanyi('servicenumber.member.privilege.owner'), value: 'Owner' },
      { label: this.i18n.fanyi('servicenumber.member.privilege.manager'), value: 'Manager' },
      { label: this.i18n.fanyi('servicenumber.member.privilege.common'), value: 'Common' }
    ];
  }

  // 初始化表单
  initForm(): void {
    this.memberForm = this.fb.group({
      memberId: [null, [Validators.required]],
      memberName: [null],
      privilege: ['Common', [Validators.required]]
    });
  }

  // 加载成员列表
  loadMemberList(): void {
    if (this.isAddSN) {
      // 如果是新增模式，使用本地数据
      this.memberList = [...this.localMembers];
      this.total = this.localMembers.length;
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    const requestId = ++this.memberListRequestId;
    const params = {
      serviceNumberId: this.serviceNumberId,
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      refreshTime: this.refreshTime,
      direction: this.direction
    };
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_MEMBER_LIST, params)
      .pipe(
        finalize(() => {
          if (requestId === this.memberListRequestId) {
            this.loading = false;
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe({
        next: res => {
          if (requestId !== this.memberListRequestId) {
            return;
          }
          if (res.success) {
            this.memberList = res.data.items || [];
            this.total = res.data.totalCount || 0;
            this.cdr.detectChanges();
          } else {
            this.message.error(this.i18n.fanyi('servicenumber.member.load-failed'));
          }
        },
        error: () => {
          if (requestId === this.memberListRequestId) {
            this.message.error(this.i18n.fanyi('servicenumber.member.load-failed'));
          }
        }
      });
  }

  refreshMemberList(): void {
    this.resetMemberListSnapshot(true);
  }

  pageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadMemberList();
  }

  pageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadMemberList();
  }

  // 打开添加成员模态框
  showAddMemberModal(): void {
    this.isEditing = false;
    this.memberForm.reset();
    this.memberForm.patchValue({ privilege: 'Common' });
    this.isModalVisible = true;
  }

  // 打开编辑成员模态框
  showEditMemberModal(member: any): void {
    if (!this.isAddSN && member?.privilege === 'Owner') {
      this.message.warning(this.i18n.fanyi('servicenumber.member.owner-cannot-be-edited'));
      return;
    }
    this.isEditing = true;
    this.currentMemberId = member.memberId;
    this.memberForm.patchValue({
      memberId: member.memberId,
      memberName: member.memberName,
      privilege: member.privilege
    });
    this.isModalVisible = true;
  }

  // 处理表单提交
  // 关闭模态框
  handleCancel(): void {
    this.memberForm.reset();
    this.isModalVisible = false;
  }

  // 提交表单
  handleSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    const formData = this.memberForm.value;

    if (!this.validateOwnerConstraint(formData)) {
      return;
    }

    if (this.isEditing && !this.isAddSN) {
      const targetMember = this.memberList.find(m => (m.memberId || m.id) === this.currentMemberId);
      if (targetMember?.privilege === 'Owner') {
        this.message.warning(this.i18n.fanyi('servicenumber.member.owner-cannot-be-edited'));
        this.handleCancel();
        return;
      }
    }

    if (this.isAddSN) {
      this.handleLocalMemberOperation(formData);
    } else {
      this.handleServerMemberOperation(formData);
    }
  }

  private validateForm(): boolean {
    if (this.memberForm.invalid) {
      Object.values(this.memberForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return false;
    }
    return true;
  }

  private validateOwnerConstraint(formData: any): boolean {
    if (formData.privilege === 'Owner' && this.hasOwner && this.isAddSN) {
      this.message.warning(this.i18n.fanyi('servicenumber.member.only-one-owner'));
      return false;
    }
    return true;
  }

  private handleLocalMemberOperation(formData: any): void {
    if (this.isEditing) {
      this.handleLocalMemberEdit(formData);
    } else {
      this.handleLocalMemberAdd(formData);
    }

    this.updateLocalMemberList();
    this.handleCancel();
    this.cdr.detectChanges();
  }

  private handleLocalMemberEdit(formData: any): void {
    const index = this.localMembers.findIndex(m => m.memberId === this.currentMemberId);

    if (index !== -1) {
      this.updateOwnerStatus(this.localMembers[index], formData);

      this.localMembers[index] = {
        ...this.localMembers[index],
        memberId: formData.memberId,
        memberName: formData.memberName,
        privilege: formData.privilege
      };

      if (formData.privilege === 'Owner') {
        this.hasOwner = true;
      }
    }
  }

  private handleLocalMemberAdd(formData: any): void {
    if (this.isDuplicateMember(formData.memberId)) {
      this.message.warning(this.i18n.fanyi('servicenumber.member.member-already-added'));
      return;
    }

    const newMember = this.createNewMember(formData);

    if (formData.privilege === 'Owner') {
      this.hasOwner = true;
    }

    this.localMembers.push(newMember);
  }

  private updateOwnerStatus(currentMember: any, newFormData: any): void {
    if (currentMember.privilege === 'Owner' && newFormData.privilege !== 'Owner') {
      this.hasOwner = false;
    }
  }

  private isDuplicateMember(memberId: string): boolean {
    return this.localMembers.findIndex(m => m.memberId === memberId) !== -1;
  }

  private createNewMember(formData: any): any {
    return {
      id: Date.now().toString(),
      memberId: formData.memberId,
      memberName: formData.memberName,
      privilege: formData.privilege
    };
  }

  private updateLocalMemberList(): void {
    this.hasOwner = this.localMembers.some(member => member.privilege === 'Owner');
    this.memberList = [...this.localMembers];
    this.total = this.localMembers.length;
    this.membersChange.emit(this.localMembers);
  }

  loadAvailableEmployees(append = false): void {
    if (!this.tenantId) {
      this.availableEmployees = [];
      this.employeeTotal = 0;
      this.hasMoreEmployees = false;
      return;
    }
    if (append && (!this.hasMoreEmployees || this.employeeLoading)) {
      return;
    }

    this.employeeLoading = true;
    const params = {
      tenantId: this.tenantId,
      pageIndex: Math.max(0, this.employeePageIndex - 1),
      pageSize: this.employeePageSize,
      direction: 'desc',
      refreshTime: Date.now(),
      key: this.memberSearchKeyword
    };

    this.http
      .post(ConstantUtil.TENANT_EMPLOYEE_LIST, params)
      .pipe(
        finalize(() => {
          this.employeeLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success && res.data) {
          const items = (res.data.items || []).filter((employee: any) => employee.status === 'Enable');
          this.availableEmployees = append ? [...this.availableEmployees, ...items] : items;
          this.employeeTotal = res.data.totalCount || 0;
          this.hasMoreEmployees = this.employeePageIndex * this.employeePageSize < this.employeeTotal && items.length > 0;
        } else {
          this.availableEmployees = append ? this.availableEmployees : [];
          this.employeeTotal = append ? this.employeeTotal : 0;
          this.hasMoreEmployees = false;
        }
      });
  }

  onInlineSearchChange(value: string): void {
    this.memberSearchKeyword = value;
    this.resetAvailableEmployees();
  }

  onCandidateListScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target || this.employeeLoading || !this.hasMoreEmployees) {
      return;
    }

    const threshold = 40;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (!isNearBottom) {
      return;
    }

    this.employeePageIndex += 1;
    this.loadAvailableEmployees(true);
  }

  addInlineMember(employee: any): void {
    if (this.isDuplicateMember(employee.id)) {
      return;
    }

    this.localMembers.push({
      id: Date.now().toString(),
      memberId: employee.id,
      memberName: employee.name,
      privilege: this.hasOwner ? 'Common' : 'Owner',
      avatarUrl: employee.avatarUrl || employee.avatar || '',
      email: employee.email || employee.account || '',
      departmentName: employee.departmentName || employee.department?.name || employee.groupName || ''
    });
    this.updateLocalMemberList();
    this.cdr.detectChanges();
  }

  updateInlinePrivilege(member: any, privilege: string): void {
    if (privilege === 'Owner') {
      this.localMembers = this.localMembers.map(current =>
        current.memberId === member.memberId
          ? { ...current, privilege: 'Owner' }
          : current.privilege === 'Owner'
            ? { ...current, privilege: 'Common' }
            : current
      );
    } else {
      this.localMembers = this.localMembers.map(current => (current.memberId === member.memberId ? { ...current, privilege } : current));
    }

    this.updateLocalMemberList();
    this.cdr.detectChanges();
  }

  removeInlineMember(member: any): void {
    this.deleteMember(member.id);
  }

  private resetAvailableEmployees(): void {
    this.employeePageIndex = 1;
    this.employeeTotal = 0;
    this.hasMoreEmployees = true;
    this.loadAvailableEmployees();
  }

  getEmployeeSubtitle(employee: any): string {
    const fragments = [
      employee.email || employee.account,
      employee.departmentName || employee.department?.name || employee.groupName
    ].filter(Boolean);
    return fragments.join(' · ');
  }

  private handleServerMemberOperation(formData: any): void {
    this.isConfirmLoading = true;
    const submitData = {
      ...formData,
      serviceNumberId: this.serviceNumberId
    };

    if (this.isEditing) {
      submitData.id = this.currentMemberId;
      this.updateMember(submitData);
    } else {
      this.addMember(submitData);
    }
  }

  // 添加成员
  addMember(data: any): void {
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_MEMBER_ADD, data)
      .pipe(
        finalize(() => {
          this.isConfirmLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.member.add-success'));
            this.resetMemberListSnapshot(true);
            this.handleCancel();
          } else {
            this.message.error(this.i18n.fanyi('servicenumber.member.add-failed'));
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('servicenumber.member.add-failed'));
        }
      });
  }

  // 更新成员
  updateMember(data: any): void {
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_MEMBER_UPDATE, data)
      .pipe(
        finalize(() => {
          this.isConfirmLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.member.update-success'));
            this.resetMemberListSnapshot();
            this.handleCancel();
          } else {
            this.message.error(this.i18n.fanyi('servicenumber.member.update-failed'));
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('servicenumber.member.update-failed'));
        }
      });
  }

  // 删除成员
  deleteMember(memberId: string): void {
    if (this.isAddSN) {
      // 新增模式下，直接操作本地数据
      const index = this.localMembers.findIndex(m => m.id === memberId);
      if (index !== -1) {
        // 如果删除的是Owner，更新hasOwner状态
        if (this.localMembers[index].privilege === 'Owner') {
          this.hasOwner = false;
        }

        this.localMembers.splice(index, 1);
        this.memberList = [...this.localMembers];
        this.total = this.localMembers.length;

        // 发送成员变更事件
        this.membersChange.emit(this.localMembers);
        this.cdr.detectChanges();
      }
    } else {
      this.loading = true;
      this.cdr.detectChanges();
      const params = {
        memberIds: [memberId],
        serviceNumberId: this.serviceNumberId
      };
      this.http.post(ConstantUtil.ADMIN_SERVICENUMBER_MEMBER_DELETE, params).subscribe({
        next: res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.member.delete-success'));
            this.resetMemberListSnapshot();
          } else {
            this.loading = false;
            this.message.error(res.msg ?? this.i18n.fanyi('servicenumber.member.delete-failed'));
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.message.error(this.i18n.fanyi('servicenumber.member.delete-failed'));
          this.cdr.detectChanges();
        }
      });
    }
  }

  showTransferButton(member: any): boolean {
    return !this.isAddSN && member.privilege !== 'Owner' && this.canTransferOwnership();
  }

  getPrivilegeTagColor(privilege: string): string {
    switch (privilege) {
      case 'Owner':
        return 'red';
      case 'Manager':
        return 'blue';
      case 'Common':
        return 'green';
      default:
        return 'default';
    }
  }

  getPrivilegeLabel(privilege: string): string {
    const privilegeOption = this.privilegeOptions.find(option => option.value === privilege);
    return privilegeOption ? privilegeOption.label : privilege;
  }

  openEmployeeSelector(): void {
    const modal = this.modalService.create({
      nzTitle: this.i18n.fanyi('servicenumber.member.select-employee'),
      nzContent: TenantEmployeeComponent,
      nzWidth: '1000px',
      nzStyle: { top: '24px' },
      nzBodyStyle: {
        maxHeight: 'calc(100vh - 96px)',
        overflow: 'hidden',
        padding: '24px'
      },
      nzFooter: null,
      nzData: {
        tenantId: this.tenantId,
        isSelector: true
      }
    });

    // 添加选择员工的事件处理
    modal.afterClose.subscribe(result => {
      if (result && result.name && result.id) {
        this.memberForm.patchValue({
          memberName: result.name
        });
        this.memberForm.patchValue({
          memberId: result.id
        });
      }
    });
  }
  // 转移拥有者权限
  transferOwnership(memberId: string): void {
    // 檢查是否為個人租戶
    if (this.tenantType === 'Person') {
      this.message.warning(this.i18n.fanyi('servicenumber.member.personal-tenant-cannot-transfer'));
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    const data = {
      serviceNumberId: this.serviceNumberId,
      memberId: memberId,
      privilege: 'Owner'
    };

    this.http.post(ConstantUtil.TENANT_SERVICENUMBER_MEMBER_OWNER_TRANSFER, data).subscribe({
      next: res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('servicenumber.member.transfer-success'));
          this.optimisticTransferOwnership(memberId);
          this.resetMemberListSnapshot(true);
        } else {
          this.loading = false;
          this.message.error(res.msg ?? this.i18n.fanyi('servicenumber.member.transfer-failed'));
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.message.error(this.i18n.fanyi('servicenumber.member.transfer-failed'));
        this.cdr.detectChanges();
      }
    });
  }

  private optimisticTransferOwnership(targetMemberId: string): void {
    if (!this.memberList || this.memberList.length === 0) {
      return;
    }
    this.memberList = this.memberList.map(member => {
      const isTarget = member.memberId === targetMemberId || member.id === targetMemberId;
      if (isTarget) {
        return { ...member, privilege: 'Owner' };
      }
      if (member.privilege === 'Owner') {
        return { ...member, privilege: 'Common' };
      }
      return member;
    });
    this.hasOwner = true;
    this.cdr.detectChanges();
  }

  // 檢查是否可以轉移權限
  canTransferOwnership(): boolean {
    return this.tenantType !== 'Person';
  }
  // 获取可用的权限选项（排除拥有者）
  getAvailablePrivilegeOptions(): any[] {
    if (this.isAddSN) {
      return this.privilegeOptions;
    } else {
      return this.privilegeOptions.filter(option => option.value !== 'Owner');
    }
  }

  private resetMemberListSnapshot(resetToFirstPage = false): void {
    if (resetToFirstPage) {
      this.pageIndex = 1;
    }
    // Let the server establish the snapshot so a client clock offset cannot hide newly changed members.
    this.refreshTime = 0;
    this.loadMemberList();
  }
}
